import { FC, Fragment, useReducer, useState } from 'react';

import { Gem, Settings, SquarePen, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';

import { TournamentFormDialog } from '../../tournamentFormDialog/TournamentFormDialog';

import { PayoutBalanceDialog } from './payoutBalanceDialog/PayoutBalanceDialog';
import { mapTournamentToForm } from './utils';

import css from './TournamentActionsColumn.module.css';

import { tournamentsService } from 'v2/api/tournamentsService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { Nullable } from 'v2/types';
import { TournamentForm, TournamentModel } from 'v2/types/TournamentModel';
import { handleHttpError } from 'v2/utils';

interface TournamentActionsColumnProps {
  tournament: TournamentModel;
  onRevalidate: VoidFunction;
}

export const TournamentActionsColumn: FC<TournamentActionsColumnProps> = ({ tournament, onRevalidate }) => {
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);

  const [isOpenEditDialog, setOpenEditDialog] = useReducer((state) => !state, false);
  const [isOpenPayoutDialog, setOpenPayoutDialog] = useReducer((state) => !state, false);

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(
    ['updateTournament', tournament.id],
    tournamentsService.updateTournament,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('The tournament has been successfully updated');
        setOpenEditDialog();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerDelete } = useSWRMutation(
    ['markTournamentAsDeleted', tournament.id],
    tournamentsService.markTournamentAsDeleted,
    {
      onSuccess: () => {
        onRevalidate();
      },
      onError: handleHttpError,
    }
  );

  const handleSubmitEdit = ({ groupId, ...values }: TournamentForm, resetForm: VoidFunction) => {
    const formValues = { ...values, groupId: groupId ? groupId : null };

    triggerUpdate(formValues).then(() => {
      resetForm();
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseMenuItem = (cb: VoidFunction) => () => {
    handleClose();
    cb();
  };

  return (
    <Fragment>
      <div className={css.tournamentActionsColumn}>
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          <Settings width={22} height={22} />
        </IconButton>

        <Popconfirm message="Are you sure that you want to delete this tournament?" onConfirm={triggerDelete}>
          <Tooltip title="Delete" placement="top">
            <IconButton color="error">
              <Trash2 width={22} height={22} />
            </IconButton>
          </Tooltip>
        </Popconfirm>
      </div>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem className={css.tournamentActionMenuItem} onClick={handleCloseMenuItem(setOpenPayoutDialog)}>
          <Gem width={18} height={18} /> Payout Balance
        </MenuItem>

        <Divider />

        <MenuItem className={css.tournamentActionMenuItem} onClick={handleCloseMenuItem(setOpenEditDialog)}>
          <SquarePen width={18} height={18} /> Edit Tournament
        </MenuItem>
      </Menu>

      <PayoutBalanceDialog tournament={tournament} isOpen={isOpenPayoutDialog} onClose={setOpenPayoutDialog} />

      <TournamentFormDialog
        title="Edit Tournament"
        isOpen={isOpenEditDialog}
        onClose={setOpenEditDialog}
        initialValues={mapTournamentToForm(tournament)}
        isSubmitting={isMutating}
        onSubmit={handleSubmitEdit}
      />
    </Fragment>
  );
};
